import {
  DEFAULT_SESSION_SHARE,
  DEFAULT_SESSION_SHARE_VALUES,
  type DefaultSessionShare,
} from './default-session-share.js';
import { resourceGrantAccessOf, type ResourceGrantAccess } from './resource-grant.types.js';

/**
 * The org-level ceiling on session sharing: which of the two share access
 * levels an organization lets its members hand out at all.
 *
 * This is a separation-of-duties control, not a UI preference. An org that
 * turns `edit` off is stating that no member may ever obtain write access to
 * another member's session — so the flag is enforced at the grant-creation
 * boundary for EVERY caller, org admins included, and the UI only reflects
 * what the server already refuses. With both levels off, sessions are not
 * shareable at all and the share affordances disappear.
 *
 * Both default to on: an org that never touches the setting shares exactly
 * as it did before the control existed.
 */
export interface SessionSharingPolicy {
  /** Whether a session may be shared read-only ("Can view"). */
  view: boolean;
  /** Whether a session may be shared with write access ("Can edit"). */
  edit: boolean;
}

/** Sharing as it works for an org that has not restricted it. */
export const DEFAULT_SESSION_SHARING_POLICY: SessionSharingPolicy = { view: true, edit: true };

/**
 * Read the policy off an organization. The wire fields are optional (legacy
 * payloads and fixtures predate them), and an absent field means the org
 * never restricted that level — so it reads as allowed, which is also what
 * the server's NOT NULL DEFAULT true column stores.
 */
export function sessionSharingPolicy(
  org: { allowSessionShareView?: boolean; allowSessionShareEdit?: boolean } | null | undefined,
): SessionSharingPolicy {
  return {
    view: org?.allowSessionShareView ?? true,
    edit: org?.allowSessionShareEdit ?? true,
  };
}

/** The access levels a share may confer, in the order the UI offers them. */
export function allowedShareAccessLevels(
  policy: SessionSharingPolicy,
): readonly ResourceGrantAccess[] {
  return (['view', 'edit'] as const).filter((access) => policy[access]);
}

/** True when the org allows no sharing at all — the share affordance is hidden. */
export function isSessionSharingDisabled(policy: SessionSharingPolicy): boolean {
  return !policy.view && !policy.edit;
}

/**
 * The access level a grant role confers, or `null` for a role outside the
 * two sharing roles. A share the policy has to judge must map to a known
 * level: anything else (an admin conferring an arbitrary org role on a
 * session) cannot be proven read-only, so a restricted org refuses it —
 * see {@link sessionShareRoleRefusal}.
 */
export function shareAccessOfRoleId(roleId: string): ResourceGrantAccess | null {
  return resourceGrantAccessOf(roleId) ?? null;
}

/** Where an org owner changes the policy, named in every refusal. */
const ASK_AN_OWNER =
  'Ask an organization owner to change this in the organization Sharing settings.';

/**
 * Why this org refuses to confer `roleId` on a session, or `null` when it
 * allows it. The single rule both the API and the settings UI are written
 * against:
 *
 *   - a role that maps to a level the org turned off is refused;
 *   - a role that maps to no level at all is refused as soon as the org
 *     restricts anything. Such a role is one an admin supplied directly
 *     (the share UI only ever writes the two), and nothing about it proves
 *     it stays inside the levels still allowed — an org role can carry
 *     write slugs under a read-only-sounding name. Refusing it is what
 *     stops "some other role" from being the backdoor around the switch
 *     the owner just flipped.
 */
export function sessionShareRoleRefusal(
  policy: SessionSharingPolicy,
  roleId: string,
): string | null {
  const access = shareAccessOfRoleId(roleId);
  if (access === null) {
    if (policy.view && policy.edit) return null;
    const allowed = allowedShareAccessLevels(policy);
    return allowed.length === 0
      ? `This organization does not allow sharing sessions. ${ASK_AN_OWNER}`
      : `This organization limits how sessions can be shared, so a share may only confer ` +
          `${allowed.join(' or ')} access. ${ASK_AN_OWNER}`;
  }
  if (policy[access]) return null;
  if (isSessionSharingDisabled(policy)) {
    return `This organization does not allow sharing sessions. ${ASK_AN_OWNER}`;
  }
  return access === 'edit'
    ? `This organization does not allow sharing sessions with edit access. ${ASK_AN_OWNER}`
    : `This organization does not allow sharing sessions with view access. ${ASK_AN_OWNER}`;
}

/**
 * The stored default on any row or wire payload. Text rather than the
 * union, because a `text` column and a legacy payload can both carry
 * something outside it; {@link storedDefaultSessionShare} is what narrows.
 */
export interface StoredDefaultSessionShareSource {
  defaultSessionShare?: string | null;
}

/** Everything needed to resolve a default against the ceiling over it. */
export interface DefaultSessionShareSource extends StoredDefaultSessionShareSource {
  allowSessionShareView?: boolean;
  allowSessionShareEdit?: boolean;
}

function isDefaultSessionShare(value: unknown): value is DefaultSessionShare {
  return DEFAULT_SESSION_SHARE_VALUES.includes(value as DefaultSessionShare);
}

/**
 * How open each default is, relative to the others. `DEFAULT_SESSION_SHARE_VALUES`
 * is declared least-open first, so its index IS the ordering — reading it here
 * rather than re-listing the levels is what keeps the clamp, the narrowing in
 * {@link minDefaultSessionShare}, and {@link defaultSessionShareCovers} agreeing
 * on one order instead of three hand-rolled comparisons that can drift apart.
 */
function opennessOf(value: DefaultSessionShare): number {
  return DEFAULT_SESSION_SHARE_VALUES.indexOf(value);
}

/** The less open of two defaults. Narrowing is always the safe direction. */
export function minDefaultSessionShare(
  a: DefaultSessionShare,
  b: DefaultSessionShare,
): DefaultSessionShare {
  return opennessOf(a) <= opennessOf(b) ? a : b;
}

/**
 * One candidate in the precedence chain {@link resolveSessionShareDefault}
 * walks.
 *
 * `value: null` means "this layer has no opinion", which is deliberately
 * distinct from an explicit `'private'`: a layer that chose private is
 * answering the question and stops the walk, while an unset one defers to the
 * layer below. The org's column cannot express the difference (it is NOT NULL
 * DEFAULT 'private'), and does not need to — the org is the last layer. A
 * per-user default is not the last layer, so the distinction is the whole
 * reason this is modelled as a chain rather than a pair of fields.
 */
export interface SessionShareDefaultLayer {
  /** What this layer chose, or `null` when it has not chosen. */
  value: DefaultSessionShare | null;
  /**
   * A limit this layer may not exceed, applied before the org-wide ceiling.
   * Where a *ceiling* says what is permitted at all, this says how far one
   * layer may depart from the layer under it — the shape an org policy of
   * "members may narrow the org default, not widen it" takes.
   */
  cap?: DefaultSessionShare;
}

/**
 * Resolve a session's starting share from an ordered precedence chain,
 * most specific layer first, then clamp the answer against the org's sharing
 * ceiling.
 *
 * First layer with an opinion wins; its own `cap` narrows it; the ceiling
 * narrows it again. A chain where nobody chose resolves to
 * {@link DEFAULT_SESSION_SHARE}.
 *
 * The ceiling clamp is the important half and the reason resolution is
 * centralised at all: `ensureResourceGrant` refuses a share the policy
 * forbids, so an org that set "everyone can edit" and later turned edit
 * sharing off would otherwise have every session creation fail on a grant
 * nobody asked for at launch time. Reading a forbidden default as `private`
 * makes a contradictory setting inert instead of fatal, and leaves the stored
 * intent intact so re-enabling the level restores it.
 */
export function resolveSessionShareDefault(
  layers: readonly SessionShareDefaultLayer[],
  policy: SessionSharingPolicy,
): DefaultSessionShare {
  const chosen = layers.find((layer) => layer.value !== null);
  if (!chosen?.value) return DEFAULT_SESSION_SHARE;
  const capped = chosen.cap ? minDefaultSessionShare(chosen.value, chosen.cap) : chosen.value;
  if (capped === 'private') return 'private';
  return policy[capped] ? capped : 'private';
}

/**
 * The default the org stored, before the ceiling is applied. This is the
 * value the settings control binds to and never the value a surface acts
 * on — an org that turned a level off keeps its recorded intent here, so
 * turning the level back on restores it. Use
 * {@link effectiveDefaultSessionShare} to decide what actually happens.
 */
export function storedDefaultSessionShare(
  org: StoredDefaultSessionShareSource | null | undefined,
): DefaultSessionShare {
  const stored = org?.defaultSessionShare;
  return isDefaultSessionShare(stored) ? stored : DEFAULT_SESSION_SHARE;
}

/**
 * The org's own default, resolved against the org's own ceiling: what a
 * session created right now starts as, absent any more specific layer. The
 * org-shaped convenience wrapper over {@link resolveSessionShareDefault} —
 * a single-layer chain, because the org is the last word on a default and
 * nothing today sits above it.
 *
 * Surfaces that later grow a more specific layer (a per-member default)
 * should build the chain and call the resolver directly rather than
 * widening this; keeping the org-only reading available under its own name
 * is what lets the settings page say "this is what the ORG chose" without
 * having to unpick a resolved answer.
 */
export function effectiveDefaultSessionShare(
  org: DefaultSessionShareSource | null | undefined,
): DefaultSessionShare {
  if (!org) return DEFAULT_SESSION_SHARE;
  return resolveSessionShareDefault(
    [{ value: storedDefaultSessionShare(org) }],
    sessionSharingPolicy(org),
  );
}

/** Whether the ceiling is currently overriding the stored default. */
export function isDefaultSessionShareClamped(
  org: DefaultSessionShareSource | null | undefined,
): boolean {
  return storedDefaultSessionShare(org) !== effectiveDefaultSessionShare(org);
}

/** The access level a default confers, or `null` when sessions start private. */
export function defaultSessionShareAccess(value: DefaultSessionShare): ResourceGrantAccess | null {
  return value === 'private' ? null : value;
}

/**
 * Whether an org-wide default at `value` already confers `access` on every
 * member. Sharing is cumulative, so an `edit` default covers a `view` ask
 * as well as an `edit` one.
 */
export function defaultSessionShareCovers(
  value: DefaultSessionShare,
  access: ResourceGrantAccess,
): boolean {
  return opennessOf(value) >= opennessOf(access);
}
